import { createContext, useContext, useEffect, useState } from "react";
import { User as FirebaseUser, onAuthStateChanged, signOut, getAdditionalUserInfo } from "firebase/auth";
import { auth, signInWithGoogle, getGoogleCredential, signInWithGoogleCredential, trackEvent } from "@/lib/firebase";
import { User, UserRole } from "@/lib/schema";
import { getCurrentUser, createUserDocumentIfMissing, updateUser } from '@/api/users';

// Augment the window interface to include the google object
declare global {
    interface Window {
      google: any;
    }
}

interface AuthContextType {
  currentUser: FirebaseUser | null;
  userData: User | null;
  loading: boolean;
  logout: () => Promise<void>;
  refetchUser: () => Promise<void>;
  signInWithGoogle: (onSuccess?: () => void, role?: UserRole) => Promise<void>;
  isNewGoogleUser: boolean;
  completeGoogleSignUp: (username: string) => Promise<void>;
  completeProfileSetup: (username: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<FirebaseUser | null>(null);
  const [userData, setUserData] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isNewGoogleUser, setIsNewGoogleUser] = useState(false);

  const completeGoogleSignUp = async (username: string) => {
    if (!currentUser) {
      const error = new Error("No current user found to complete sign-up.");
      console.error(error.message);
      throw error;
    }
    
    try {
      await updateUser(currentUser.uid, { username, onboardingCompleted: true });
      await refetchUser();
      setIsNewGoogleUser(false);
      
      // Track successful signup completion
      trackEvent('sign_up', {
        method: 'google',
        user_id: currentUser.uid,
      });
    } catch (error) {
      console.error("Error completing Google sign-up:", error);
      throw error;
    }
  };

  const completeProfileSetup = async (username: string) => {
    if (!currentUser) {
      const error = new Error("No current user found to complete profile setup.");
      console.error(error.message);
      throw error;
    }
    
    try {
      await updateUser(currentUser.uid, { username, onboardingCompleted: true });
      setIsNewGoogleUser(false); // Reset the flag regardless of sign-in method
    } catch (error) {
      console.error("Error completing profile setup:", error);
      throw error;
    }
  };

  const fetchUserData = async (firebaseUser: FirebaseUser, role?: UserRole) => {
    try {
      let userData = await getCurrentUser();
      if (userData) {
        setUserData(userData);
        // Do NOT set isNewGoogleUser here - it should only be set during actual Google sign-in
        return;
      }
      
      const createdUser = await createUserDocumentIfMissing(role);
      if (createdUser) {
        setUserData(createdUser);
        // Do NOT set isNewGoogleUser here - it should only be set during actual Google sign-in
      } else {
        console.error("Failed to create user document - user will be signed out");
        await signOut(auth);
        setUserData(null);
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
      if (error instanceof Error && error.message.includes('ZodError')) {
        try {
          const createdUser = await createUserDocumentIfMissing(role);
          if (createdUser) {
            setUserData(createdUser);
            // Do NOT set isNewGoogleUser here - it should only be set during actual Google sign-in
            return;
          }
        } catch (recreateError) {
          console.error("Failed to recreate user document:", recreateError);
        }
      }
      console.error("Unable to resolve user data issues - signing out user");
      await signOut(auth);
      setUserData(null);
    }
  };
  
  const signInWithGoogleProxy = async (onSuccess?: () => void, role?: UserRole) => {
    try {
        const result = await signInWithGoogle();
        const additionalUserInfo = getAdditionalUserInfo(result);
        
        // Set the flag for new users or existing Google users who haven't completed onboarding
        if (additionalUserInfo?.isNewUser) {
          setIsNewGoogleUser(true);
          // For new users, we need to create their profile with the specified role
          if (role) {
            await fetchUserData(result.user, role);
          }
        } else {
          // For existing users, check if they need onboarding completion
          // We'll check this after user data is fetched
          const currentUserData = await getCurrentUser();
          if (currentUserData && !currentUserData.onboardingCompleted) {
            // Only set to true if they signed in via Google (check provider)
            const providerId = result.user.providerData[0]?.providerId;
            if (providerId === 'google.com') {
              setIsNewGoogleUser(true);
            }
          } else {
            // Track login for existing users with completed profiles
            trackEvent('login', {
              method: 'google',
              user_id: result.user.uid,
            });
          }
        }
        
        // Call the success callback after successful authentication
        if (onSuccess) {
          onSuccess();
        }
      } catch (error) {
        console.error("Error during Google sign-in:", error);
        // Rethrow the error so UI components can handle it properly
        throw error;
      }
  }
  
  const refetchUser = async () => {
    if (currentUser) {
      await fetchUserData(currentUser);
    }
  };

  const logout = async () => {
    // Track logout before clearing state
    if (currentUser) {
      trackEvent('logout', {
        user_id: currentUser.uid,
      });
    }
    
    await signOut(auth);
    setUserData(null);
    setIsNewGoogleUser(false);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        setLoading(true);
        try {
          await fetchUserData(user);
        } catch (error) {
          console.error("Error fetching user data in auth state change:", error);
          // Don't sign out the user here as fetchUserData already handles that
        } finally {
          setLoading(false);
        }
      } else {
        setUserData(null);
        setIsNewGoogleUser(false);
        setLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  // Separate effect for Google One Tap initialization
  useEffect(() => {
    const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    const isDevelopment = import.meta.env.DEV;

    if (!GOOGLE_CLIENT_ID || isDevelopment) {
      return; // Skip in development or if no client ID
    }

    let initialized = false;
    let retryCount = 0;
    const maxRetries = 10;

    const waitForGoogleScript = () => {
      return new Promise<void>((resolve, reject) => {
        const checkGoogle = () => {
          if (window.google && window.google.accounts && window.google.accounts.id) {
            resolve();
          } else if (retryCount < maxRetries) {
            retryCount++;
            setTimeout(checkGoogle, 200); // Check every 200ms
          } else {
            reject(new Error('Google script failed to load after retries'));
          }
        };
        checkGoogle();
      });
    };

    const initializeGoogleOneTap = async () => {
      if (initialized) return;

      try {
        await waitForGoogleScript();
        
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          use_fedcm_for_prompt: true,
          auto_select: false,
          cancel_on_tap_outside: true,
          itp_support: true, // Improved tracking prevention support
          callback: async (response: any) => {
            const credential = getGoogleCredential(response.credential);
            try {
              const result = await signInWithGoogleCredential(credential);
              const additionalUserInfo = getAdditionalUserInfo(result);
              
              // Set the flag for new users or existing Google users who haven't completed onboarding
              if (additionalUserInfo?.isNewUser) {
                setIsNewGoogleUser(true);
              } else {
                // For existing users, check if they need onboarding completion
                const currentUserData = await getCurrentUser();
                if (currentUserData && !currentUserData.onboardingCompleted) {
                  // This is definitely a Google sign-in (One Tap)
                  setIsNewGoogleUser(true);
                }
              }
            } catch (error) {
              console.error("Error signing in with Google One Tap:", error);
            }
          },
        });

        initialized = true;
        console.debug("Google One Tap initialized successfully");
      } catch (error) {
        console.debug("Google One Tap initialization failed:", error);
      }
    };

    // Initialize Google One Tap
    initializeGoogleOneTap();
  }, []);

  // Separate effect for prompting One Tap when auth state changes
  useEffect(() => {
    const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    const isDevelopment = import.meta.env.DEV;

    if (!GOOGLE_CLIENT_ID || isDevelopment || loading) {
      return; // Skip if no client ID, in development, or still loading auth state
    }

    // Only prompt if user is definitely not signed in and auth state has settled
    if (currentUser === null && !loading) {
      const promptOneTap = () => {
        if (window.google && window.google.accounts && window.google.accounts.id) {
          try {
            // With FedCM enabled, the notification callback is optional and deprecated methods removed
            // Simply call prompt without a callback or with a minimal one
            window.google.accounts.id.prompt();
          } catch (error) {
            console.debug("Google One Tap prompt error:", error);
          }
        }
      };

      // Wait a bit to ensure DOM is ready and user has had time to see the page
      const promptTimer = setTimeout(promptOneTap, 2000);
      return () => clearTimeout(promptTimer);
    }
  }, [currentUser, loading]); // Re-run when auth state changes


  const value = {
    currentUser,
    userData,
    loading,
    logout,
    refetchUser,
    signInWithGoogle: signInWithGoogleProxy,
    isNewGoogleUser,
    completeGoogleSignUp,
    completeProfileSetup,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
